import { apiSlice } from './api-slice';
import {
  Response,
  Sortable,
  Project,
  PaginatedProject,
  TeamMember,
} from '../models';
import { PAGINATION_SIZE } from '../constants';

export const projectsApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getAllProjects: builder.query<
      Response<
        Sortable<
          Pick<
            Project,
            | 'projectName'
            | 'projectCode'
            | 'calculatedStartDate'
            | 'calculatedEndDate'
            | 'calculatedProgress'
            | 'calculatedTeamMembers'
            | 'id'
            | 'projectStatusEnum'
            | 'projectThumbnailImageURL'
            | 'projectTypeEnum'
          >
        >
      >,
      PaginatedProject
    >({
      query: params => ({
        url: `company/project`,
        method: 'GET',
        params: { size: PAGINATION_SIZE, ...params },
      }),
      providesTags: result =>
        result?.data.content.length
          ? result.data.content.map(({ id }) => ({ type: 'Project', id }))
          : ['Project'],
    }),
    getProjectById: builder.query<Response<Project>, number>({
      query: projectId => ({
        url: `company/project/${projectId}`,
        method: 'GET',
      }),
      providesTags: (_, __, id) => [{ type: 'Project', id }],
    }),
    createProject: builder.mutation<Response<Project>, FormData>({
      query: data => ({
        url: 'company/project',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Project' }],
    }),
    updateProject: builder.mutation<Response<Project>, FormData>({
      query: data => ({
        url: 'company/project',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: result => {
        return [{ type: 'Project', id: result?.data.id }];
      },
    }),
    deleteProject: builder.mutation<Response<null>, { projectId: number }>({
      query: ({ projectId }) => ({
        url: `company/project/${projectId}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => {
        return [{ type: 'Project' }];
      },
    }),
    getTeamMemberByProject: builder.query<
      Response<TeamMember[]>,
      {
        projectId: string;
      }
    >({
      query: ({ projectId }) => ({
        url: `company/project/${projectId}/teamMember`,
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useGetProjectByIdQuery,
  useGetAllProjectsQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useGetTeamMemberByProjectQuery,
} = projectsApiSlice;
